import { ChangeDetectionStrategy, Component, computed, effect, inject, Signal } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';

import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { PhOverlayRef } from '@ph-core/services/overlay';
import { LazyTemplateService } from '@ph-core/services/template/lazy-template.service';
import { CreditInsuranceCoverageOption } from '@ph-model/form/credit-insurance-coverage-option.model';
import { OptionType } from '@ph-model/form/option-type.model';
import { CartStore, CartStoreType } from '@ph-shopping/store/cart/cart.store';
import { PrismicStore, PrismicStoreType } from '@ph-store/prismic/prismic.store';

import { FormInputControlComponent } from '../form-input-control/form-input-control.component';
import { IconComponent } from '../icon/icon-component';
import { PhButtonComponent } from '../ph-button/ph-button.component';
import { Coverage } from '@ph-shopping/model/cart/request-quote';

@Component({
  templateUrl: './requested-quote-popup.component.html',
  styleUrls: ['./requested-quote-popup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, FormInputControlComponent, PhButtonComponent, TranslateModule, IconComponent],
})
export class RequestQuotePopupComponent {
  fb = inject(FormBuilder);
  cartStore: CartStoreType = inject(CartStore);
  requestQuoteForm: FormGroup = this.fb.group({ coverages: this.fb.array([]) });
  prismicStore: PrismicStoreType = inject(PrismicStore);

  private translate = inject(TranslateService);
  private ref = inject<PhOverlayRef<RequestQuotePopupComponent>>(PhOverlayRef);
  private templateService = inject(LazyTemplateService);
  private _navbarLogo: Signal<string> = computed(() => {
    const isFrenchSelected: boolean = localStorage.getItem('languageCode') === 'fr-CA';
    const data = this.prismicStore.data();

    return (isFrenchSelected ? data?.small_logo_fr.url || data?.small_logo.url : data?.small_logo.url) ?? '';
  });

  constructor() {
    effect(() => {
      const requestedQuote = this.cartStore.requestedQuote();
      const loanProtectionCoverageOptions = this.cartStore.loanProtectionCoverageOptions();
      const creditInsurancePackageCoverageOptions = this.cartStore.creditInsurancePackageCoverageOptions();
      const coverageDetails = requestedQuote?.insResponse.insurance.coverage;

      if (coverageDetails && coverageDetails.length > 0) {
        const coverageOptionDescriptions = [
          ...loanProtectionCoverageOptions,
          ...creditInsurancePackageCoverageOptions,
        ].reduce((acc: { [key: string]: string }, option: OptionType | CreditInsuranceCoverageOption) => {
          if (typeof option.value === 'string') {
            acc[option.value] = this.translate.instant(option.description);
          } else {
            acc[option.value.single] = this.translate.instant(option.description);
            acc[option.value.joint] = this.translate.instant(option.description);
          }

          return acc;
        }, {});

        const arr: FormArray = this.requestQuoteForm.get('coverages') as FormArray;
        while (arr.length) {
          arr.removeAt(0);
        }

        const groupedByBorrower: {
          [key: string]: {
            coverage: Coverage;
            coverageNames: string[];
            terms: string[];
            premiums: string[];
            taxes: string[];
          };
        } = {};

        for (const cov of coverageDetails) {
          const coverages = groupedByBorrower[cov.borrCovered] || {
            coverage: cov,
            coverageNames: [],
            terms: [],
            premiums: [],
            taxes: [],
          };
          coverages.coverageNames.push(coverageOptionDescriptions[cov.covCode] || cov.covCode);
          coverages.premiums.push(cov.premium ?? '');
          coverages.taxes.push(cov.tax ?? '');
          coverages.terms.push(cov.insTerm.toString());
          groupedByBorrower[cov.borrCovered] = coverages;
        }

        for (const key of Object.keys(groupedByBorrower)) {
          const value = groupedByBorrower[key];
          arr.push(
            this.fb.group({
              ...value.coverage,
              coverage: value.coverageNames.join(', '),
              insTerm: value.terms.join(', '),
              premium: this._formatSum(value.premiums),
              tax: this._formatSum(value.taxes),
              borrower: this._getBorrowerLabel(value.coverage.borrCovered),
            })
          );
        }
      }
    });
  }

  get coverages(): FormArray {
    return this.requestQuoteForm.get('coverages') as FormArray;
  }

  printQuote(): void {
    void this.templateService.printQuote(this._navbarLogo());
    this.ref.close();
  }

  close(): void {
    this.ref.close();
  }

  private _getBorrowerLabel(borrowerCode: string): string {
    switch (borrowerCode) {
      default:
      case '1':
        return this.translate.instant('shoppingPage.checkout.grid.primaryBorrower');
      case '2':
        return this.translate.instant('shoppingPage.checkout.grid.coBorrower');
      case '3':
        return this.translate.instant('shoppingPage.checkout.grid.jointBorrower');
    }
  }

  private _formatSum(values: string[]): string {
    if (values.length === 1) {
      return values[0];
    }

    return (
      values.join(', ') + ' (Total ' + values.reduce((sum: number, prem: string) => sum + +prem, 0).toFixed(2) + ')'
    );
  }
}
